import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  ShieldCheck,
  Truck,
  MapPin,
  DollarSign,
  Wrench,
  ArrowRight,
  CheckCircle,
  Phone,
  Mail,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ELDERS_LOGO =
  'https://customer-assets-wrfwihn1.emergentagent.net/job_a0e7f17d-9159-4d2a-a971-2aaa914eeb6a/artifacts/p12muae5_image.png';

const TOW_METHODS = [
  { value: '', label: 'Select tow method…' },
  { value: 'self_drive', label: 'Self-drive / driven under own power' },
  { value: 'low_loader', label: 'Low loader / float' },
  { value: 'tilt_tray', label: 'Tilt tray' },
  { value: 'flat_top', label: 'Flat-top / drop deck' },
  { value: 'extendable', label: 'Extendable trailer' },
  { value: 'crane_lift', label: 'Crane lift & load' },
  { value: 'other', label: 'Other (describe in load notes)' },
];

const INTEREST_OPTIONS = [
  { key: 'marine_carriers', label: 'Marine Carriers Insurance' },
  { key: 'public_liability', label: 'Public Liability Insurance' },
  { key: 'motor_fleet', label: 'Motor / Fleet Insurance' },
  { key: 'trailers_in_control', label: 'Trailers in Control Insurance' },
  { key: 'machinery_load', label: 'Machinery / Load Specific Insurance' },
  { key: 'pilot_insurance', label: 'Pilot Insurance' },
  { key: 'steerer_insurance', label: 'Steerer Insurance' },
  { key: 'business_pack', label: 'Business Pack' },
  { key: 'accident_sickness', label: 'Accident / Sickness Insurance' },
];

const initialForm = {
  contact_name: '',
  business_name: '',
  abn: '',
  address: '',
  phone: '',
  email: '',
  machinery_value: '',
  serial_number: '',
  description: '',
  radius_km: '',
  components_attached: '',
  tow_method: '',
  interests: [],
};

export default function InsuranceQuotes() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleInterest = (key) =>
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(key)
        ? f.interests.filter((k) => k !== key)
        : [...f.interests, key],
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        machinery_value: form.machinery_value ? Number(form.machinery_value) : null,
        radius_km: form.radius_km ? Number(form.radius_km) : null,
      };
      await axios.post(`${API}/insurance-quotes`, payload);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          'Something went wrong — please try emailing us directly at info@trucknsendit.com.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="insurance-quotes-page">
      <Header />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#c8102e] via-[#dc2626] to-[#8b0f1e] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-white rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-[#1e3a5f] rounded-full blur-3xl" />
        </div>
        <div className="relative container mx-auto px-4 py-12 sm:py-20">
          <div className="grid md:grid-cols-[1.4fr_1fr] gap-8 items-center">
            <div>
              <span className="inline-block bg-white text-red-700 text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow mb-4">
                Cover for the load, the machine and the move
              </span>
              <h1 className="text-3xl sm:text-5xl font-bold leading-tight mb-4 max-w-3xl">
                Insurance quotes for heavy machinery, oversize loads &amp; transport.
              </h1>
              <p className="text-base sm:text-lg text-red-50 max-w-2xl mb-6">
                Truckn Send It has partnered with{' '}
                <span className="font-semibold text-white">Elders Insurance</span> — one of
                Australia&apos;s most trusted rural &amp; commercial insurers — to give operators
                a fast, no-obligation quote on covering the gear you&apos;re moving.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() =>
                    document
                      .getElementById('insurance-form')
                      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }
                  className="bg-white hover:bg-red-50 text-red-700 shadow-lg font-semibold"
                  data-testid="insurance-hero-form-cta"
                >
                  <ShieldCheck className="w-4 h-4 mr-2" /> Get my quote
                </Button>
                <Button
                  variant="outline"
                  onClick={() => (window.location.href = 'tel:1300380700')}
                  className="border-white/70 text-white hover:bg-white hover:text-red-700"
                  data-testid="insurance-call-cta"
                >
                  <Phone className="w-4 h-4 mr-2" /> Call 1300 380 700
                </Button>
              </div>
            </div>

            <div className="flex justify-center md:justify-end">
              <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-2xl text-center max-w-xs w-full">
                <p className="text-[10px] font-bold uppercase tracking-widest text-red-700 mb-3">
                  In partnership with
                </p>
                <img
                  src={ELDERS_LOGO}
                  alt="Elders Insurance"
                  className="mx-auto max-h-24 object-contain"
                  data-testid="insurance-elders-logo"
                />
                <p className="mt-4 text-xs text-gray-600 leading-relaxed">
                  Elders Insurance has protected Australian farmers, businesses and operators for
                  generations. Quotes are handled by licensed Elders brokers.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="container mx-auto px-4 py-10 sm:py-14">
        <div className="grid sm:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {[
            {
              icon: Truck,
              title: 'Machinery &amp; load cover',
              desc: 'Insure the machine value and any components while in transit, whether self-driven or transported.',
            },
            {
              icon: MapPin,
              title: 'Coverage tailored to route',
              desc: 'Tell us the radius of the move — from a 20 km yard shift to interstate relocations.',
            },
            {
              icon: ShieldCheck,
              title: 'Real broker, no bots',
              desc: 'An Elders Insurance broker will review your details and reach out with an honest quote.',
            },
          ].map((v, idx) => {
            const Icon = v.icon;
            return (
              <Card
                key={idx}
                className="p-5 border-l-4 border-l-red-600 hover:shadow-lg transition-shadow"
                data-testid={`insurance-value-${idx}`}
              >
                <div className="w-11 h-11 rounded-xl bg-red-50 text-red-600 flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5" />
                </div>
                <h3
                  className="font-bold text-[#1e3a5f] text-lg mb-1"
                  dangerouslySetInnerHTML={{ __html: v.title }}
                />
                <p className="text-sm text-gray-600 leading-relaxed">{v.desc}</p>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Form */}
      <section className="flex-1 container mx-auto px-4 pb-14" id="insurance-form">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1e3a5f] mb-2">
              Tell us about the machine &amp; the move
            </h2>
            <p className="text-gray-600 text-sm">
              Takes about 2 minutes. An Elders broker will be in touch the same business day.
            </p>
          </div>

          <Card className="p-6 sm:p-8 shadow-lg border-t-4 border-t-red-600">
            {submitted ? (
              <div className="text-center py-8" data-testid="insurance-form-success">
                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-lg font-bold text-[#1e3a5f] mb-1">
                  Thanks — your quote request is in.
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  An Elders Insurance broker (via Truckn Send It) will contact you the same business
                  day.
                </p>
                <div className="flex gap-3 justify-center">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSubmitted(false);
                      setForm(initialForm);
                    }}
                    data-testid="insurance-form-again"
                  >
                    Submit another quote
                  </Button>
                  <Button
                    onClick={() => navigate('/')}
                    className="bg-[#1e3a5f] hover:bg-[#254d7f] text-white"
                    data-testid="insurance-form-home"
                  >
                    Back to home
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="insurance-quote-form">
                {/* Business */}
                <div>
                  <h4 className="text-sm font-bold text-[#1e3a5f] uppercase tracking-wider mb-3 flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-red-600" /> Business details
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="iq-contact">Contact name *</Label>
                      <Input
                        id="iq-contact"
                        value={form.contact_name}
                        onChange={(e) => update('contact_name', e.target.value)}
                        placeholder="Your full name"
                        required
                        data-testid="insurance-contact-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="iq-business">Business name</Label>
                      <Input
                        id="iq-business"
                        value={form.business_name}
                        onChange={(e) => update('business_name', e.target.value)}
                        placeholder="ACME Earthmoving Pty Ltd (optional)"
                        data-testid="insurance-business-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="iq-abn">ABN *</Label>
                      <Input
                        id="iq-abn"
                        value={form.abn}
                        onChange={(e) => update('abn', e.target.value)}
                        placeholder="11 222 333 444"
                        required
                        data-testid="insurance-abn-input"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <Label htmlFor="iq-address">Business address</Label>
                    <Input
                      id="iq-address"
                      value={form.address}
                      onChange={(e) => update('address', e.target.value)}
                      placeholder="123 Depot Rd, Wagga Wagga NSW"
                      data-testid="insurance-address-input"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label htmlFor="iq-phone">Phone *</Label>
                      <Input
                        id="iq-phone"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => update('phone', e.target.value)}
                        placeholder="0400 000 000"
                        required
                        data-testid="insurance-phone-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="iq-email">Email *</Label>
                      <Input
                        id="iq-email"
                        type="email"
                        value={form.email}
                        onChange={(e) => update('email', e.target.value)}
                        placeholder="you@example.com"
                        required
                        data-testid="insurance-email-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Products of interest — MOVED to top so machine/load details only show when needed */}
                <div className="pt-2 border-t border-gray-100">
                  <h4 className="text-sm font-bold text-[#1e3a5f] uppercase tracking-wider mb-3 flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-red-600" /> I need
                  </h4>
                  <p className="text-xs text-gray-500 mb-3">
                    Tick the cover types you&apos;d like an Elders broker to quote on. Tick <strong>Machinery / Load Specific Insurance</strong> to enter machine and move details below.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {INTEREST_OPTIONS.map((opt) => {
                      const checked = form.interests.includes(opt.key);
                      return (
                        <label
                          key={opt.key}
                          className={`flex items-center gap-3 rounded-md border p-3 cursor-pointer text-sm transition-colors ${
                            checked
                              ? 'border-red-600 bg-red-50/60 text-[#1e3a5f] font-semibold'
                              : 'border-gray-200 hover:border-gray-300 text-gray-700'
                          }`}
                          data-testid={`insurance-interest-${opt.key}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleInterest(opt.key)}
                            className="w-4 h-4 accent-red-600 flex-shrink-0"
                            data-testid={`insurance-interest-input-${opt.key}`}
                          />
                          <span>{opt.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Machine details — only shown when machinery/load is ticked */}
                {form.interests.includes('machinery_load') && (
                <div className="pt-2 border-t border-gray-100" data-testid="insurance-machine-section">
                  <h4 className="text-sm font-bold text-[#1e3a5f] uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Wrench className="w-4 h-4 text-red-600" /> Machine / load details
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="iq-value">
                        Machinery value (AUD) *
                      </Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          id="iq-value"
                          type="number"
                          min="0"
                          step="1000"
                          value={form.machinery_value}
                          onChange={(e) => update('machinery_value', e.target.value)}
                          placeholder="250000"
                          className="pl-9"
                          data-testid="insurance-machinery-value-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="iq-serial">Serial / VIN number</Label>
                      <Input
                        id="iq-serial"
                        value={form.serial_number}
                        onChange={(e) => update('serial_number', e.target.value)}
                        placeholder="CAT-XYZ-123456"
                        data-testid="insurance-serial-input"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <Label htmlFor="iq-desc">Description of load *</Label>
                    <Textarea
                      id="iq-desc"
                      value={form.description}
                      onChange={(e) => update('description', e.target.value)}
                      placeholder="e.g. 2019 CAT 336 Excavator, 30-tonne class, in working order, being relocated from job site to yard."
                      rows={4}
                      data-testid="insurance-description-input"
                    />
                  </div>
                  <div className="mt-4">
                    <Label htmlFor="iq-components">Components / attachments included</Label>
                    <Input
                      id="iq-components"
                      value={form.components_attached}
                      onChange={(e) => update('components_attached', e.target.value)}
                      placeholder="e.g. Bucket, hammer, tilt hitch, quick-hitch, ripper"
                      data-testid="insurance-components-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      List any attached tools or components you want covered.
                    </p>
                  </div>
                </div>
                )}

                {/* Move details — only shown when machinery/load is ticked */}
                {form.interests.includes('machinery_load') && (
                <div className="pt-2 border-t border-gray-100" data-testid="insurance-move-section">
                  <h4 className="text-sm font-bold text-[#1e3a5f] uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Truck className="w-4 h-4 text-red-600" /> The move
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="iq-radius">Radius to move (km)</Label>
                      <Input
                        id="iq-radius"
                        type="number"
                        min="0"
                        value={form.radius_km}
                        onChange={(e) => update('radius_km', e.target.value)}
                        placeholder="e.g. 350"
                        data-testid="insurance-radius-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="iq-tow">Tow / transport method</Label>
                      <select
                        id="iq-tow"
                        value={form.tow_method}
                        onChange={(e) => update('tow_method', e.target.value)}
                        className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                        data-testid="insurance-tow-method-select"
                      >
                        {TOW_METHODS.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                )}

                {error && (
                  <p className="text-sm text-red-600" data-testid="insurance-form-error">
                    {error}
                  </p>
                )}

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-6 text-base shadow"
                  data-testid="insurance-form-submit"
                >
                  {submitting ? 'Sending…' : 'Request my quote'}
                  {!submitting && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  Prefer email?{' '}
                  <a href="mailto:info@trucknsendit.com" className="underline">
                    info@trucknsendit.com
                  </a>{' '}
                  · Prefer to call?{' '}
                  <a href="tel:1300380700" className="underline">
                    1300 380 700
                  </a>
                </p>
                <p className="text-[11px] text-gray-400 text-center leading-relaxed">
                  Your details are shared with Elders Insurance solely to prepare a quote. No cover
                  is bound until confirmed in writing by an authorised Elders broker.
                </p>
              </form>
            )}
          </Card>
        </div>

        <div className="mt-10 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-sm text-gray-500 hover:text-gray-700 underline"
            data-testid="insurance-back-home"
          >
            ← Back to home
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
